// A plan swap Chargebee holds as a ramp, as returned by GET /subscriptions/find-one
// (`scheduledPlanChange` / `lastPlanChange`). `toPlanPrice` is in dollars.
export type PlanChange = {
  rampId: string;
  fromItemPriceId: string | null;
  toItemPriceId: string;
  toPlanName: string | null;
  toPlanPrice: number | null;
  effectiveAt: string;
};

export type SubscriptionNotice =
  | { kind: 'cancellation'; date: string }
  | { kind: 'scheduledPlanChange'; change: PlanChange }
  | { kind: 'planChanged'; change: PlanChange };

const DISMISSED_KEY_PREFIX = 'planChangedNoticeDismissed:';

export const formatNoticeDate = (date: string): string =>
  new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

export const planLabel = (change: PlanChange): string => {
  const name = change.toPlanName || 'your new plan';
  return change.toPlanPrice ? `${name} ($${change.toPlanPrice}/month)` : name;
};

// localStorage can throw (private mode, blocked storage); a failed read means
// "not dismissed" and a failed write only means the notice comes back.
export const isPlanChangeDismissed = (rampId: string): boolean => {
  try {
    return localStorage.getItem(`${DISMISSED_KEY_PREFIX}${rampId}`) === '1';
  } catch {
    return false;
  }
};

export const dismissPlanChange = (rampId: string): void => {
  try {
    localStorage.setItem(`${DISMISSED_KEY_PREFIX}${rampId}`, '1');
  } catch {
    // ignore
  }
};

/**
 * The one subscription notice the dashboard shows, most urgent first: a
 * scheduled cancellation, then a scheduled plan change, then a plan change
 * that already happened and the member has not dismissed.
 */
export const selectSubscriptionNotice = ({
  isScheduledForCancellation,
  scheduledCancelDate,
  scheduledPlanChange,
  lastPlanChange,
  isDismissed,
}: {
  isScheduledForCancellation: boolean;
  scheduledCancelDate: string | null;
  scheduledPlanChange: PlanChange | null;
  lastPlanChange: PlanChange | null;
  isDismissed: (rampId: string) => boolean;
}): SubscriptionNotice | null => {
  if (isScheduledForCancellation && scheduledCancelDate) {
    return { kind: 'cancellation', date: scheduledCancelDate };
  }
  if (scheduledPlanChange) {
    return { kind: 'scheduledPlanChange', change: scheduledPlanChange };
  }
  if (lastPlanChange && !isDismissed(lastPlanChange.rampId)) {
    return { kind: 'planChanged', change: lastPlanChange };
  }
  return null;
};
